import React, { useCallback, useEffect, useState } from 'react'
import { CustomContentModal } from '../../../components/CustomContentModal'
import Button from '../../../components/Form/Button'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../shared/redux/hooks'
import { updateRateSheetApi } from '../../../apis/apis'
import type { TariffHistoryAttachment, TariffRateSheetCategory } from '../../../apis/types'
import { FieldTooltip, PCIA_YEAR_HINT } from './TariffFieldKey'

function documentDateInput(value: string | null | undefined): string {
    if (value == null || value === '') return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const inputClass =
    'block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'

export const TariffEditRateSheetModal: React.FC<{
    open: boolean
    attachment: TariffHistoryAttachment | null
    onClose: () => void
    onSaved: (attachment: TariffHistoryAttachment) => void
}> = ({ open, attachment, onClose, onSaved }) => {
    const dispatch = useAppDispatch()
    const [name, setName] = useState('')
    const [documentDate, setDocumentDate] = useState('')
    const [category, setCategory] = useState<TariffRateSheetCategory>('INDIVIDUAL')
    const [vintageYear, setVintageYear] = useState('')
    const [isShared, setIsShared] = useState(false)
    const [saving, setSaving] = useState(false)

    const resetFromAttachment = useCallback((sheet: TariffHistoryAttachment) => {
        setName(sheet.label)
        setDocumentDate(documentDateInput(sheet.uploadedAt))
        setCategory(sheet.category ?? 'INDIVIDUAL')
        setVintageYear(sheet.vintageYear ?? '')
        setIsShared(sheet.isShared === true)
        setSaving(false)
    }, [])

    useEffect(() => {
        if (open && attachment != null) {
            resetFromAttachment(attachment)
        }
    }, [open, attachment, resetFromAttachment])

    const canSave = name.trim() !== '' && !saving
    const appliesEverywhere = attachment?.isShared === true || (attachment?.linkCount ?? 0) > 1

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault()
        if (attachment == null || !canSave) return

        setSaving(true)
        try {
            const updated = await updateRateSheetApi(attachment.cuid, {
                label: name.trim(),
                category,
                vintageYear: vintageYear.trim() !== '' ? vintageYear.trim() : null,
                isShared,
                documentDate: documentDate !== '' ? documentDate : undefined,
            })
            onSaved(updated)
            void dispatch(pushToast({ type: 'success', message: 'Rate sheet updated' }))
            onClose()
        } catch {
            void dispatch(pushToast({ type: 'error', message: 'Failed to update rate sheet' }))
        } finally {
            setSaving(false)
        }
    }

    return (
        <CustomContentModal isOpen={open} onClose={() => !saving && onClose()}>
            <form
                onSubmit={(e) => void handleSubmit(e)}
                className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900"
            >
                <h3 className="text-lg font-medium text-slate-900 dark:text-white">Edit rate sheet</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Update the name, category, vintage, document date, or sharing. To replace the file, upload a
                    new sheet and remove this one from the rateset.
                </p>
                {appliesEverywhere ? (
                    <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
                        This sheet is linked on other ratesets. Saving updates every rateset that uses it.
                    </p>
                ) : null}

                <div className="mt-4 space-y-3">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                        Name
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={`${inputClass} mt-1`}
                            required
                        />
                    </label>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                        Category
                        <select
                            value={category}
                            onChange={(e) => {
                                const next = e.target.value as TariffRateSheetCategory
                                setCategory(next)
                                if (next === 'GROUP') setIsShared(true)
                            }}
                            className={`${inputClass} mt-1`}
                        >
                            <option value="INDIVIDUAL">Individual</option>
                            <option value="GROUP">Group</option>
                        </select>
                    </label>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                        <FieldTooltip content={PCIA_YEAR_HINT}>PCIA year (optional)</FieldTooltip>
                        <input
                            type="text"
                            value={vintageYear}
                            onChange={(e) => setVintageYear(e.target.value)}
                            className={`${inputClass} mt-1`}
                            placeholder="e.g. 2024"
                        />
                    </label>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                        Document date
                        <input
                            type="date"
                            value={documentDate}
                            onChange={(e) => setDocumentDate(e.target.value)}
                            className={`${inputClass} mt-1`}
                        />
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <input
                            type="checkbox"
                            checked={isShared}
                            onChange={(e) => setIsShared(e.target.checked)}
                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Share with other tariffs on this utility
                    </label>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <Button type="button" color="slate" disabled={saving} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={!canSave}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                </div>
            </form>
        </CustomContentModal>
    )
}
